<template>
  <div class="profile-dropdown-wrapper">
    <VaDropdown v-model="isShown" :offset="[9, 0]" class="profile-dropdown" stick-to-edges>
      <template #anchor>
        <VaButton preset="secondary" color="textPrimary">
          <span class="profile-dropdown__anchor min-w-max">
            <slot />
            <!-- <VaAvatar :size="32" color="warning"> 😍 </VaAvatar> -->
            
            <VaAvatar :size="32" color="warning"> 
              <span style="font-size: 16px;">{{ user?.name ? user.name.split(' ').length > 0 ?  user.name.split(' ').length == 1 ? user.name.split(' ')[0].substring(0,2) : user.name.split(' ')[0].substring(0,1) + user.name.split(' ')[user.name.split(' ').length - 1].substring(0,1)  : '😍' : '😍' }}</span> 
            </VaAvatar>
          </span>
        </VaButton>
      </template>
      <VaDropdownContent
        class="profile-dropdown__content md:w-60 px-0 py-4 w-full"
        :style="{ '--hover-color': hoverColor }"
      >
        <VaList v-for="group in options" :key="group.name">
          <!-- A group may name its own header key. Without one the header comes
               from `user.<group name>`, as it always has — `headerKey` exists
               because `user.language` holds the English string "Change Language"
               in BOTH locale files, a leftover of the template, and a German
               screen must not say that. Correcting that value would change a
               maintained string in de.json; overriding the key here changes
               nothing that was already in use. -->
          <header v-if="group.name" class="uppercase text-[var(--va-secondary)] opacity-80 font-bold text-xs px-4">
            {{ t(group.headerKey ?? `user.${group.name}`) }}
          </header>

          <template v-for="item in group.list">
            <VaListItem
              v-if="!item.locale && item.name != 'logout'"
              :key="item.name"
              class="menu-item px-4 text-base cursor-pointer h-8"
              v-bind="resolveLinkAttribute(item)"
            >
              <VaIcon :name="item.icon" class="pr-1" color="secondary" />
              {{ t(`user.${item.name}`) }}
            </VaListItem>

            <!-- Language entries: same list item, but they switch the locale
                 instead of navigating — like the logout entry below, which is
                 this component's existing pattern for an acting entry. Its own
                 v-if rather than a v-else-if, as the logout entry has: the
                 branches are already mutually exclusive through `item.locale`.
                 The language in effect is marked with a check icon. -->
            <VaListItem
              v-if="item.locale"
              :key="item.name"
              class="menu-item px-4 text-base cursor-pointer h-8"
              @click="selectLocale(item)"
            >
              <VaIcon
                :name="activeLocale === item.locale ? 'mso-check' : item.icon"
                class="pr-1"
                :color="activeLocale === item.locale ? 'primary' : 'secondary'"
              />
              {{ itemLabel(item) }}
            </VaListItem>

            <VaListItem
              :key="item.name"
              v-if="item.name == 'logout'"
              class="menu-item px-4 text-base cursor-pointer h-8"
              @click="logout()"
            >
              <VaIcon :name="item.icon" class="pr-1" color="secondary" />
              {{ t(`user.${item.name}`) }}
            </VaListItem>
          </template>

          <VaListSeparator v-if="group.separator" class="mx-3 my-2" />
        </VaList>
      </VaDropdownContent>
    </VaDropdown>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useColors } from 'vuestic-ui'

const { colors, setHSLAColor } = useColors()
const hoverColor = computed(() => setHSLAColor(colors.focus, { a: 0.1 }))

import { useRouter } from 'vue-router'

import { useAuthStore } from '@/stores/auth.store'
const authStore = useAuthStore();
const user = computed(() => authStore.user)

const { t } = useI18n()

import { setLocale, getLocale } from '@/i18n/locale'
import { SUPPORTED_LOCALES, type SupportedLocale } from '@/i18n'

// Reactive because getLocale() reads the i18n locale ref: picking a language
// re-renders the menu, so the check mark moves with the choice.
const activeLocale = computed(() => getLocale())

type ProfileListItem = {
  name: string
  to?: string
  href?: string
  icon: string
  // Set on the language entries only. Its presence is what makes an entry
  // switch the language rather than navigate.
  locale?: SupportedLocale
  // Full i18n key. The other entries are labelled `user.<name>`; the language
  // entries carry their key, because it comes from the locale list.
  labelKey?: string
}

type ProfileOptions = {
  name: string
  separator: boolean
  list: ProfileListItem[]
}

withDefaults(
  defineProps<{
    options?: ProfileOptions[]
  }>(),
  {
    options: () => [
      {
        name: 'account',
        separator: true,
        list: [
          {
            name: 'profile',
            to: 'preferences',
            icon: 'mso-account_circle',
          },
          {
            name: 'settings',
            to: 'settings',
            icon: 'mso-settings',
          },
          {
            name: 'billing',
            to: 'billing',
            icon: 'mso-receipt_long',
          },
          {
            name: 'projects',
            to: 'projects',
            icon: 'mso-favorite',
          },
        ],
      },
      {
        name: 'explore',
        separator: true,
        list: [
          {
            name: 'faq',
            to: 'faq',
            icon: 'mso-quiz',
          },
          {
            name: 'helpAndSupport',
            href: 'https://discord.gg/u7fQdqQt8c',
            icon: 'mso-error',
          },
        ],
      },
      {
        name: 'language',
        // ⚠️ NOT `user.language`: that key holds "Change Language" in de.json
        // as well as gb.json, so the German menu would carry an English
        // heading. `language.title` is added beside `language.german` /
        // `language.english`, where the two language names already live.
        headerKey: 'language.title',
        separator: true,
        // Built from the fixed list in i18n/index.ts rather than from the
        // loaded locale files, so the menu can never offer a language this
        // project does not maintain.
        list: SUPPORTED_LOCALES.map((entry) => ({
          name: entry.code,
          labelKey: entry.labelKey,
          locale: entry.code,
          icon: 'mso-language',
        })),
      },
      {
        name: 'logout',
        separator: false,
        list: [
          {
            name: 'logout',
            to: 'logout',
            icon: 'mso-logout',
          },
        ],
      },
    ],
  },
)

const isShown = ref(false)

// The language entries carry their own i18n key, because it comes from the
// locale list in i18n/index.ts; every other entry in this menu is labelled
// `user.<name>`. Resolved here rather than in the template so the key is a
// string either way — `labelKey` is optional on the type.
const itemLabel = (item: ProfileListItem) => t(item.labelKey ?? `user.${item.name}`)

// setLocale() stores the choice as well, so the language survives a reload.
// An entry without a locale is not a language entry and is left alone.
const selectLocale = (item: ProfileListItem) => {
  if (item.locale) {
    setLocale(item.locale)
  }
}

const resolveLinkAttribute = async (item: ProfileListItem) => {
  return item.to ? { to: { name: item.to } } : item.href ? { href: item.href, target: '_blank' } : {}
}

const { push } = useRouter()
const logout = async () => {
  console.log('logout')
  
  authStore.LOGOUT().then(() => {
    push({ name: 'login' })
  });
}
</script>

<style lang="scss">
.profile-dropdown {
  cursor: pointer;

  &__content {
    .menu-item:hover {
      background: var(--hover-color);
    }
  }

  &__anchor {
    display: inline-block;
  }
}
</style>
